import * as React from 'react';
import type { Node } from '@xyflow/react';
import { useCanvasStore, type CanvasState } from '@features/flows/state/canvas-store';
import type { NodeTemplate } from '@features/flows/palette/registry';
import NodeAttrsForm from './NodeAttrsForm';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import type { NodeBindings, BindingSpec } from '@features/flows/utils/types';
import { shallow } from 'zustand/shallow';

type Props = {
  nodeId: string | null;
  templateRegistry: Map<string, NodeTemplate> | null;
  onChangeAttrs: (nodeId: string, next: any) => void;
  onChangeLabel?: (nodeId: string, next: string) => void;
  onTogglePin?: (nodeId: string, field: string) => void;
  onChangeBinding?: (nodeId: string, field: string, spec: Partial<BindingSpec>) => void;
};

type SelectedNode = {
  id: string;
  label: string;
  attrs: any;
  bindings: NodeBindings | undefined;
  templateKey?: string;
  handler?: string;
  ui?: unknown;
};

function NodeConfigPanel({ nodeId, templateRegistry, onChangeAttrs, onChangeLabel, onTogglePin, onChangeBinding }: Props) {
  const lastSnapshotRef = React.useRef<SelectedNode | null>(null);

  React.useEffect(() => {
    if (!nodeId) {
      lastSnapshotRef.current = null;
    }
  }, [nodeId]);

  const selectNodeSnapshot = React.useMemo(() => {
    if (!nodeId) {
      return () => null as SelectedNode | null;
    }
    const targetId = nodeId;
    return (state: CanvasState) => {
      const node = state.graph.nodes.find((n) => n.id === targetId);
      if (!node) {
        lastSnapshotRef.current = null;
        return null;
      }
      const data = (node.data ?? {}) as any;
      const label = data.label ?? node.id;
      const attrs = data.attrs;
      const bindings = data.bindings;
      const templateKey = data.templateKey;
      const handler = data.handler;
      const ui = data.ui;
      const prev = lastSnapshotRef.current;
      if (
        prev &&
        prev.id === node.id &&
        prev.label === label &&
        prev.attrs === attrs &&
        prev.bindings === bindings &&
        prev.templateKey === templateKey &&
        prev.handler === handler &&
        prev.ui === ui
      ) {
        return prev;
      }
      const snapshot: SelectedNode = {
        id: node.id,
        label,
        attrs,
        bindings,
        templateKey,
        handler,
        ui,
      };
      lastSnapshotRef.current = snapshot;
      return snapshot;
    };
  }, [nodeId]);

  const selectedNode = useCanvasStore(selectNodeSnapshot, shallow);

  const selectedNodeId = selectedNode?.id ?? nodeId ?? null;
  const templateKey = selectedNode?.templateKey || selectedNode?.handler;
  const selectedNodeUi = selectedNode?.ui;

  const template = React.useMemo(() => {
    if (!templateKey || !templateRegistry) return undefined;
    return templateRegistry.get(String(templateKey));
  }, [templateKey, templateRegistry]);

  const nodeStub = React.useMemo<Pick<Node, 'id' | 'data'> | undefined>(() => {
    if (!selectedNodeId) return undefined;
    const data: any = {};
    if (selectedNodeUi) data.ui = selectedNodeUi;
    return { id: selectedNodeId, data };
  }, [selectedNodeId, selectedNodeUi]);

  const handleAttrsChange = React.useCallback((next: any) => {
    if (!selectedNodeId) return;
    onChangeAttrs(selectedNodeId, next);
  }, [selectedNodeId, onChangeAttrs]);

  const handleLabelChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedNodeId) return;
    onChangeLabel?.(selectedNodeId, (e.target as HTMLInputElement).value);
  }, [selectedNodeId, onChangeLabel]);

  const handleTogglePin = React.useCallback((field: string) => {
    if (!selectedNodeId) return;
    onTogglePin?.(selectedNodeId, field);
  }, [selectedNodeId, onTogglePin]);

  const handleBindingChange = React.useCallback((field: string, spec: Partial<BindingSpec>) => {
    if (!selectedNodeId) return;
    onChangeBinding?.(selectedNodeId, field, spec);
  }, [selectedNodeId, onChangeBinding]);

  if (!selectedNodeId || !selectedNode) {
    return (
      <aside className="h-full w-full bg-background flex flex-col">
        <div className="h-10 flex items-center px-3 border-b text-sm font-medium">Node</div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="p-3 text-sm text-muted-foreground">Select a node to edit its attributes.</div>
        </div>
      </aside>
    );
  }

  const label = selectedNode.label;
  const attrs = selectedNode.attrs;
  const nodeBindings = selectedNode.bindings;

  return (
    <aside className="h-full w-full bg-background flex flex-col">
      <div className="h-10 flex items-center justify-between px-3 border-b">
        <div className="text-sm font-medium truncate" title={label}>{label}</div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="p-3 space-y-3">
          <div className="text-xs text-muted-foreground">
            <div><span className="font-medium">ID:</span> <span className="font-mono">{selectedNode.id}</span></div>
            {template && (
              <div className="truncate" title={template.key}>
                <span className="font-medium">Template:</span> <span className="font-mono">{template.key}</span>
              </div>
            )}
          </div>
          <Separator />
          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">Title</div>
            <Input
              value={label}
              onChange={handleLabelChange}
              placeholder="Node title"
            />
          </div>
          <Separator />
          <NodeAttrsForm
            label={label}
            template={template}
            value={attrs}
            onChange={handleAttrsChange}
            bindings={nodeBindings}
            onTogglePin={handleTogglePin}
            onChangeBinding={handleBindingChange}
            node={nodeStub}
          />
        </div>
      </div>
    </aside>
  );
}

NodeConfigPanel.displayName = 'NodeConfigPanel';

export default React.memo(NodeConfigPanel);
